import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, EyeOff } from "lucide-react";
import { useAuthDialog } from "../../contexts/AuthDialogContext";
import { useAuth } from "../../contexts/AuthContext";
import { authApi } from "../../api/authApi";
import { ExtendedRegistrationRequest } from "../../types/user";

const AuthDialog = () => {
  const { isAuthDialogOpen, authDialogMode, closeAuthDialog, switchAuthMode } = useAuthDialog();
  const { login } = useAuth();
  
  // Состояния для форм
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Состояния для расширенной регистрации
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newsletterSubscription, setNewsletterSubscription] = useState(false);

  // Состояния для юридического лица
  const [entityType, setEntityType] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [inn, setInn] = useState('');
  const [kpp, setKpp] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [contactPersonName, setContactPersonName] = useState('');
  const [contactPersonPhone, setContactPersonPhone] = useState('');
  const [contactPersonEmail, setContactPersonEmail] = useState('');

  // Очистка форм при смене режима
  useEffect(() => {
    setUsername('');
    setPassword('');
    setEmail('');
    setError('');
    setSuccess('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFirstName('');
    setLastName('');
    setPhone('');
    setConfirmPassword('');
    setNewsletterSubscription(false);
    setEntityType('');
    setCompanyName('');
    setInn('');
    setKpp('');
    setCompanyPhone('');
    setLegalAddress('');
    setContactPersonName('');
    setContactPersonPhone('');
    setContactPersonEmail('');
  }, [authDialogMode]);

  const handleClose = useCallback(() => {
    setUsername('');
    setPassword('');
    setEmail('');
    setError('');
    setSuccess('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFirstName('');
    setLastName('');
    setPhone('');
    setConfirmPassword('');
    setNewsletterSubscription(false);
    setEntityType('');
    setCompanyName('');
    setInn('');
    setKpp('');
    setCompanyPhone('');
    setLegalAddress('');
    setContactPersonName('');
    setContactPersonPhone('');
    setContactPersonEmail('');
    closeAuthDialog();
  }, [closeAuthDialog]);

  // Обработка клавиши Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isAuthDialogOpen) {
        handleClose();
      }
    };

    if (isAuthDialogOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAuthDialogOpen, handleClose]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await authApi.login(username, password);
      
      if (response.success && response.accessToken && response.refreshToken) {
        login(response.accessToken, response.refreshToken);
        handleClose();
        
        // Перезагружаем страницу для обновления всех данных
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        setError(response.message || 'Ошибка входа');
      }
    } catch (err) {
      setError('Произошла ошибка при входе');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExtendedRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const registrationData: ExtendedRegistrationRequest = {
        firstName,
        lastName,
        email,
        phone,
        password,
        confirmPassword,
        newsletterSubscription
      };

      const response = await authApi.registerExtended(registrationData);
      
      if (response.success) {
        setSuccess('Вы успешно зарегистрировались');
        setTimeout(() => {
          switchAuthMode('login');
        }, 2000);
      } else {
        setError(response.message || 'Ошибка регистрации');
      }
    } catch (err) {
      setError('Произошла ошибка при регистрации');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLegalEntityApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const applicationData = {
        entityType,
        companyName,
        inn,
        kpp,
        companyPhone,
        legalAddress,
        contactPersonName,
        contactPersonPhone,
        contactPersonEmail
      };

      const response = await authApi.createLegalEntityApplication(applicationData);
      
      if (response.success) {
        setSuccess('Заявка успешно отправлена. Мы свяжемся с вами в ближайшее время.');
        setTimeout(() => {
          switchAuthMode('login');
        }, 3000);
      } else {
        setError(response.message || 'Ошибка отправки заявки');
      }
    } catch (err) {
      setError('Произошла ошибка при отправке заявки');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestorePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await authApi.restorePassword(email);
      if (response.success) {
        setSuccess('Инструкции по восстановлению пароля отправлены на ваш email.');
        setTimeout(() => {
          switchAuthMode('login');
        }, 2000);
      } else {
        setError(response.message || 'Ошибка при восстановлении пароля');
      }
    } catch (err) {
      setError('Произошла ошибка при восстановлении пароля');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="space-y-4">
      <div>
        <label htmlFor="username" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Электронная почта</label>
        <div className="relative">
          <input
            id="username"
            type="email"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
            placeholder="Электронная почта"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
      </div>
      
      <div>
        <label htmlFor="password" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Пароль</label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            required
            className="w-full px-4 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
            placeholder="Пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="size-4 text-gray-400" /> : <Eye className="size-4 text-gray-400" />}
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting || !username || !password}
        className={`w-full py-2 px-4 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
          isSubmitting || !username || !password
            ? 'bg-gray-300 text-gray-500 border border-gray-300'
            : 'bg-black text-white border border-gray-300 hover:bg-gray-800'
        }`}
      >
        {isSubmitting ? 'Вход...' : 'Войти'}
      </button>

      <div className="text-center space-y-2">
        <button
          type="button"
          onClick={() => switchAuthMode('register-choice')}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          Создать аккаунт
        </button>
        <div>
          <button
            type="button"
            onClick={() => switchAuthMode('restore')}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Забыли пароль?
          </button>
        </div>
      </div>
    </form>
  );

  const renderRegisterChoiceForm = () => (
    <div className="space-y-4">
      
      <button
        type="button"
        onClick={() => switchAuthMode('register')}
        className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
      >
        Физическое лицо
      </button>
      
      <button
        type="button"
        onClick={() => switchAuthMode('legal-entity')}
        className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
      >
        Юридическое лицо
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => switchAuthMode('login')}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          Уже есть аккаунт? Войти
        </button>
      </div>
    </div>
  );

  const renderRegisterForm = () => {
    const isFormValid = firstName && email && password && confirmPassword && password === confirmPassword;
    
    return (
      <form onSubmit={handleExtendedRegister} className="space-y-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Имя*</label>
          <div className="relative">
            <input
              id="firstName"
              type="text"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
              placeholder="Введите имя"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Фамилия</label>
          <div className="relative">
            <input
              id="lastName"
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
              placeholder="Введите фамилию"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label htmlFor="register-email" className="block text-sm font-normal text-gray-700 mb-1 pl-4">E-mail*</label>
          <div className="relative">
            <input
              id="register-email"
              type="email"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
              placeholder="Введите e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Телефон</label>
          <div className="relative">
            <input
              id="phone"
              type="tel"
              className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
              placeholder="Введите телефон"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label htmlFor="register-password" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Придумайте пароль*</label>
          <div className="relative">
            <input
              id="register-password"
              type={showPassword ? "text" : "password"}
              required
              className="w-full px-4 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
              placeholder="Введите пароль первый раз"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="size-4 text-gray-400" /> : <Eye className="size-4 text-gray-400" />}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Повторите пароль*</label>
          <div className="relative">
            <input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              required
              className="w-full px-4 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
              placeholder="Введите пароль второй раз"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff className="size-4 text-gray-400" /> : <Eye className="size-4 text-gray-400" />}
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            id="newsletter"
            type="checkbox"
            className="rounded-full border-gray-300 text-gray-600 focus:ring-gray-500"
            checked={newsletterSubscription}
            onChange={(e) => setNewsletterSubscription(e.target.checked)}
          />
          <label htmlFor="newsletter" className="text-sm font-normal text-gray-700">
            Участвовать в программе лояльности
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className={`w-full py-2 px-4 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
            isSubmitting || !isFormValid
              ? 'bg-gray-300 text-gray-500 border border-gray-300'
              : 'bg-black text-white border border-gray-300 hover:bg-gray-800'
          }`}
        >
          {isSubmitting ? 'Регистрация...' : 'Зарегистрироваться'}
        </button>

        <div className="text-center text-xs text-gray-600">
          Отправляя данные, вы соглашаетесь с условиями{' '}
          <button type="button" className="text-gray-800 hover:underline">оферты</button> и{' '}
          <button type="button" className="text-gray-800 hover:underline">политики конфиденциальности</button>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={() => switchAuthMode('login')}
            className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Уже есть аккаунт? Войти
          </button>
        </div>

        <div className="text-center text-xs text-gray-500">
          *Обязательное поле
        </div>
      </form>
    );
  };

  const renderLegalEntityForm = () => {
    const isFormValid = entityType && companyName && inn && legalAddress && contactPersonName && contactPersonPhone && contactPersonEmail;
    
    return (
      <form onSubmit={handleLegalEntityApplication} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Левая колонка */}
          <div className="space-y-4">
            <div>
              <label htmlFor="entityType" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Тип организации*</label>
              <div className="relative">
                <select
                  id="entityType"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                >
                  <option value="">Выберите тип организации</option>
                  <option value="ИП">ИП</option>
                  <option value="ООО">ООО</option>
                  <option value="АО">АО</option>
                  <option value="ОАО">ОАО</option>
                  <option value="ЗАО">ЗАО</option>
                  <option value="ПАО">ПАО</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="companyName" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Название организации*</label>
              <div className="relative">
                <input
                  id="companyName"
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите название организации"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="inn" className="block text-sm font-normal text-gray-700 mb-1 pl-4">ИНН*</label>
              <div className="relative">
                <input
                  id="inn"
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите ИНН"
                  value={inn}
                  onChange={(e) => setInn(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="kpp" className="block text-sm font-normal text-gray-700 mb-1 pl-4">КПП</label>
              <div className="relative">
                <input
                  id="kpp"
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите КПП"
                  value={kpp}
                  onChange={(e) => setKpp(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="companyPhone" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Телефон организации</label>
              <div className="relative">
                <input
                  id="companyPhone"
                  type="tel"
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите телефон организации"
                  value={companyPhone}
                  onChange={(e) => setCompanyPhone(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="legalAddress" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Юридический адрес*</label>
              <div className="relative">
                <input
                  id="legalAddress"
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите юридический адрес"
                  value={legalAddress}
                  onChange={(e) => setLegalAddress(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Правая колонка */}
          <div className="space-y-4">
            <div>
              <label htmlFor="contactPersonName" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Контактное лицо*</label>
              <div className="relative">
                <input
                  id="contactPersonName"
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="ФИО контактного лица"
                  value={contactPersonName}
                  onChange={(e) => setContactPersonName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="contactPersonPhone" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Телефон контактного лица*</label>
              <div className="relative">
                <input
                  id="contactPersonPhone"
                  type="tel"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите телефон контактного лица"
                  value={contactPersonPhone}
                  onChange={(e) => setContactPersonPhone(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="contactPersonEmail" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Email контактного лица*</label>
              <div className="relative">
                <input
                  id="contactPersonEmail"
                  type="email"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите email контактного лица"
                  value={contactPersonEmail}
                  onChange={(e) => setContactPersonEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Кнопка и элементы под ней в правой колонке */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !isFormValid}
                className={`w-full py-2 px-4 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                  isSubmitting || !isFormValid
                    ? 'bg-gray-300 text-gray-500 border border-gray-300'
                    : 'bg-black text-white border border-gray-300 hover:bg-gray-800'
                }`}
              >
                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              </button>

              <div className="text-center text-xs text-gray-600 mt-4">
                Отправляя заявку, вы соглашаетесь с условиями{' '}
                <button type="button" className="text-gray-800 hover:underline">оферты</button> и{' '}
                <button type="button" className="text-gray-800 hover:underline">политики конфиденциальности</button>
              </div>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => switchAuthMode('login')}
                  className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Уже есть аккаунт? Войти
                </button>
              </div>

              <div className="text-center text-xs text-gray-500 mt-2">
                *Обязательное поле
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  };

  const renderRestoreForm = () => (
    <form onSubmit={handleRestorePassword} className="space-y-4">
      <div>
        <label htmlFor="restore-email" className="block text-sm font-normal text-gray-700 mb-1 pl-4">Электронная почта</label>
        <div className="relative">
          <input
            id="restore-email"
            type="email"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
            placeholder="Электронная почта"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting || !email}
        className={`w-full py-2 px-4 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
          isSubmitting || !email
            ? 'bg-gray-300 text-gray-500 border border-gray-300'
            : 'bg-black text-white border border-gray-300 hover:bg-gray-800'
        }`}
      >
        {isSubmitting ? 'Отправка...' : 'Восстановить пароль'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => switchAuthMode('login')}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          Вернуться на страницу входа
        </button>
      </div>
    </form>
  );

  const renderForm = () => {
    switch (authDialogMode) {
      case 'login': return renderLoginForm();
      case 'register-choice': return renderRegisterChoiceForm();
      case 'register': return renderRegisterForm();
      case 'legal-entity': return renderLegalEntityForm();
      case 'restore': return renderRestoreForm();
      default: return renderLoginForm();
    }
  };

  return (
    <AnimatePresence>
      {isAuthDialogOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`w-[98vw] rounded-lg border bg-white text-popover-foreground shadow-lg p-6 ${
              authDialogMode === 'legal-entity' ? 'max-w-4xl' : 'max-w-md'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-end mb-6">
              <button
                onClick={handleClose}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors duration-100 hover:bg-accent hover:text-accent-foreground p-1.5"
              >
                <X className="size-4" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">{success}</p>
              </div>
            )}

            {renderForm()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthDialog;
